import { daysBetween, type IsoDate } from '../dates';
import type { NutritionTotals } from '../nutrition';
import type { Cuisine, Recipe } from '../recipe';

export interface DinnerHistoryEntry {
  date: IsoDate;
  recipeId: string;
  cuisine: Cuisine;
}

export interface ScoreInput {
  recipe: Recipe;
  date: IsoDate;
  history: readonly DinnerHistoryEntry[];
  portionCostCents: number;
  targetPortionCents: number;
  nutrition: NutritionTotals;
  random: () => number;
}

// DE: Gewichte der Bewertung; Begründung in docs/specs/2026-10-16-stage-1-domain-design.md.
// EN: Score weights; rationale in docs/specs/2026-10-16-stage-1-domain-design.md.
const COST_WEIGHT = 1.5;
const REPEAT_PENALTY = 0.3;
const CUISINE_PENALTY = 0.2;
const TARGET_WEIGHT = 0.3;
const NOISE_RANGE = 0.5;
const VARIETY_DAYS = 6;

function targetsMet(nutrition: NutritionTotals): number {
  const met = [nutrition.protein >= 20, nutrition.fibre >= 6, nutrition.glycemicLoad <= 20];
  return met.filter(Boolean).length;
}

/**
 * DE: Bewertung eines Abendessens: günstig, ausgewogen, abwechslungsreich, plus etwas Zufall.
 * Gleiches Gericht innerhalb von 6 Tagen ist ausgeschlossen (-Infinity).
 * EN: Score of a dinner: cheap, balanced, varied, plus a little randomness.
 * The same dinner within 6 days is ruled out (-Infinity).
 */
export function scoreDinner(input: ScoreInput): number {
  const sameRecipe = input.history.filter((entry) => entry.recipeId === input.recipe.id);
  if (sameRecipe.some((entry) => daysBetween(entry.date, input.date) <= VARIETY_DAYS)) {
    return -Infinity;
  }
  const lastTwo = [...input.history].sort((a, b) => a.date.localeCompare(b.date)).slice(-2);
  const sameCuisine = lastTwo.filter((entry) => entry.cuisine === input.recipe.cuisine).length;
  const met = targetsMet(input.nutrition);
  return (
    -COST_WEIGHT * (input.portionCostCents / input.targetPortionCents) -
    REPEAT_PENALTY * sameRecipe.length -
    CUISINE_PENALTY * sameCuisine +
    TARGET_WEIGHT * met -
    TARGET_WEIGHT * (3 - met) +
    (input.random() - 0.5) * NOISE_RANGE
  );
}
